import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../lib/i18n';

/**
 * Veb-kameradan rasm olish — sotuvchi ekranida ham, mijozning
 * telefonida ham bir xil ishlaydi.
 *
 * MyID talablari: png/jpeg, 300 KB – 1.5 MB, yuz anfas, kesilmagan.
 * Shu sababli kadr to'liq o'lchamda va yuqori sifatda olinadi.
 */

interface CameraCaptureProps {
  submitting: boolean;
  /** Telefonda old kamera kerak, kompyuterda yagona kamera. */
  facingMode?: 'user' | 'environment';
  onCapture: (photoDataUrl: string) => void;
  /** Suratni tasdiqlash tugmasining matni. */
  confirmLabel: string;
  /** Ixtiyoriy qo'shimcha tugmalar (mock rejim uchun). */
  extra?: (photo: string) => React.ReactNode;
}

/** JPEG sifati: hajm 300 KB dan kam bo'lmasligi uchun yuqori olinadi. */
const JPEG_QUALITY = 0.95;

export function CameraCapture({
  submitting,
  facingMode = 'user',
  onCapture,
  confirmLabel,
  extra,
}: CameraCaptureProps) {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Matn emas, kalit saqlanadi — til almashsa xabar ham o'ziga o'zgaradi.
  const [cameraError, setCameraError] = useState<
    'denied' | 'notFound' | 'generic' | 'capture' | null
  >(null);
  const [ready, setReady] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch (err) {
        const name = err instanceof DOMException ? err.name : '';
        setCameraError(
          name === 'NotAllowedError'
            ? 'denied'
            : name === 'NotFoundError'
              ? 'notFound'
              : 'generic',
        );
      }
    }

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [facingMode]);

  /** Kadrni to'liq o'lchamda oladi — kesish va siqish taqiqlangan. */
  const capture = useCallback((): string | null => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  }, []);

  if (cameraError) {
    return (
      <div className="alert alert--error" role="alert">
        {t(`myid.err.${cameraError}`)}
      </div>
    );
  }

  return (
    <>
      <div className="camera">
        {/*
          Video HAR DOIM DOM'da qoladi. Agar uni olingan kadr bilan
          almashtirsak, element qayta yaratilib oqimni yo'qotadi va
          «qayta olish» dan keyin kamera ishlamay qoladi.
        */}
        <video ref={videoRef} className="camera__video" playsInline muted />
        {preview && <img className="camera__shot" src={preview} alt={t('myid.photoAlt')} />}
        {!preview && <div className="camera__oval" aria-hidden="true" />}
      </div>

      {preview ? (
        <>
          <div className="flow-nav">
            <button
              className="btn"
              type="button"
              onClick={() => setPreview(null)}
              disabled={submitting}
            >
              {t('myid.retake')}
            </button>
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => onCapture(preview)}
              disabled={submitting}
            >
              {submitting ? t('myid.checking') : confirmLabel}
            </button>
          </div>
          {extra?.(preview)}
        </>
      ) : (
        <div className="flow-nav flow-nav--end">
          <button
            className="btn btn--primary"
            type="button"
            onClick={() => {
              const photo = capture();
              if (!photo) setCameraError('capture');
              else setPreview(photo);
            }}
            disabled={!ready || submitting}
          >
            {t('myid.shoot')}
          </button>
        </div>
      )}
    </>
  );
}
